import { Card, List, Tag, Tooltip, Typography } from "antd";
import {
  CheckCircleFilled,
  ClockCircleOutlined,
  Loading3QuartersOutlined,
} from "@ant-design/icons";

export type ChecklistStatus = "pending" | "partial" | "complete";

export interface ChecklistEntry {
  status: ChecklistStatus;
  answer?: string;
  stepTitle?: string;
  question?: string;
}

export interface ChecklistTopic {
  id: string;
  stepTitle: string;
  question: string;
  description?: string;
}

interface IntakeChecklistProps {
  topics: ChecklistTopic[];
  state: Record<string, ChecklistEntry>;
}

const STATUS_LABEL: Record<ChecklistStatus, string> = {
  pending: "Pending",
  partial: "Partial",
  complete: "Complete",
};

function StatusPill({ status }: { status: ChecklistStatus }) {
  if (status === "complete") {
    return (
      <Tag color="success" icon={<CheckCircleFilled />} aria-label="Status: complete">
        {STATUS_LABEL.complete}
      </Tag>
    );
  }
  if (status === "partial") {
    return (
      <Tag color="processing" icon={<Loading3QuartersOutlined spin />} aria-label="Status: partial">
        {STATUS_LABEL.partial}
      </Tag>
    );
  }
  return (
    <Tag color="default" icon={<ClockCircleOutlined />} aria-label="Status: pending">
      {STATUS_LABEL.pending}
    </Tag>
  );
}

export default function IntakeChecklist({ topics, state }: IntakeChecklistProps) {
  const completedCount = topics.filter(
    (t) => state[t.id]?.status === "complete"
  ).length;

  return (
    <Card
      size="small"
      role="region"
      aria-label="Intake topic checklist"
      title={
        <span>
          Intake checklist{" "}
          <Typography.Text type="secondary" style={{ fontWeight: 400 }}>
            ({completedCount} / {topics.length})
          </Typography.Text>
        </span>
      }
      style={{ position: "sticky", top: 16 }}
    >
      <List
        size="small"
        dataSource={topics}
        renderItem={(topic) => {
          const entry = state[topic.id] || { status: "pending" as ChecklistStatus };
          return (
            <List.Item
              key={topic.id}
              aria-readonly="true"
              style={{ display: "block", paddingInline: 0 }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                <Tooltip title={topic.description || topic.question} placement="left">
                  <Typography.Text strong style={{ flex: 1 }}>
                    {topic.stepTitle}
                  </Typography.Text>
                </Tooltip>
                <StatusPill status={entry.status} />
              </div>
              {entry.answer ? (
                <Typography.Paragraph
                  type="secondary"
                  style={{ margin: "4px 0 0", fontSize: 12, lineHeight: 1.4 }}
                  ellipsis={{ rows: 2, tooltip: entry.answer }}
                >
                  {entry.answer}
                </Typography.Paragraph>
              ) : null}
            </List.Item>
          );
        }}
      />
    </Card>
  );
}
